// src/pages/Tracking/TrackingPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  Modal,
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-hot-toast";
import axios from "../../api/axios";

const API_URL = process.env.REACT_APP_API_URL || "https://chineafriquecargo.com";
const WHATSAPP_CACHE_KEY = "cac_whatsapp_number";
const WHATSAPP_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 heures

const TrackingPage = () => {
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [trackingInput, setTrackingInput] = useState(trackingNumber || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("13825016747");
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [inputError, setInputError] = useState("");

  // Modal pour la carte GPS
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    setAnimated(true);
    fetchWhatsappNumber();
    if (trackingNumber) {
      const cleanNumber = trackingNumber.trim().toUpperCase();
      setTrackingInput(cleanNumber);
      searchTracking(cleanNumber);
    }
  }, [trackingNumber]);

  const fetchWhatsappNumber = useCallback(async () => {
    const cached = localStorage.getItem(WHATSAPP_CACHE_KEY);
    const cachedTime = localStorage.getItem(`${WHATSAPP_CACHE_KEY}_time`);

    if (
      cached &&
      cachedTime &&
      Date.now() - parseInt(cachedTime) < WHATSAPP_CACHE_DURATION
    ) {
      setWhatsappNumber(cached);
      setLoadingWhatsapp(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/whatsapp/public/`);
      let numbers = [];
      if (response.data && response.data.results) {
        numbers = response.data.results;
      } else if (Array.isArray(response.data)) {
        numbers = response.data;
      }

      if (numbers.length > 0) {
        const defaultNum = numbers.find((n) => n.is_default) || numbers[0];
        const cleanNumber = defaultNum.phone_number
          .replace(/\s+/g, "")
          .replace(/[^0-9+]/g, "");
        setWhatsappNumber(cleanNumber);
        localStorage.setItem(WHATSAPP_CACHE_KEY, cleanNumber);
        localStorage.setItem(
          `${WHATSAPP_CACHE_KEY}_time`,
          Date.now().toString(),
        );
      }
    } catch (err) {
      console.error("Erreur chargement WhatsApp:", err);
    } finally {
      setLoadingWhatsapp(false);
    }
  }, []);

  const cleanPhoneNumber = (phone) => {
    return phone.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
  };

  const validateTrackingNumber = (number) => {
    const cleanNumber = number.trim().toUpperCase();

    if (!cleanNumber) {
      return "Veuillez entrer un numéro de suivi";
    }

    const trackingPattern = /^CAC[A-Z0-9]{6,12}$/;

    if (!trackingPattern.test(cleanNumber)) {
      return "Format invalide. Exemple : CAC12345678";
    }

    return "";
  };

  const searchTracking = async (number) => {
    const cleanNumber = number.trim().toUpperCase();

    const validationError = validateTrackingNumber(cleanNumber);
    if (validationError) {
      setError(validationError);
      setInputError(validationError);
      setTrackingData(null);
      return;
    }

    setLoading(true);
    setError("");
    setInputError("");

    try {
      const response = await axios.get(`/public/tracking/${cleanNumber}/`);
      setTrackingData(response.data);
      if (!trackingNumber) {
        navigate(`/tracking/${cleanNumber}`, { replace: true });
      }
    } catch (err) {
      console.error("Erreur tracking:", err);
      const errorMessage =
        err.response?.data?.error || "Numéro de suivi introuvable";
      setError(errorMessage);
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    searchTracking(trackingInput);
  };

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setTrackingInput(value);
    if (inputError) {
      setInputError("");
    }
  };

  const copyToClipboard = () => {
    if (trackingData?.tracking_number) {
      navigator.clipboard.writeText(trackingData.tracking_number);
      setCopied(true);
      toast.success("✅ Numéro copié dans le presse-papier !");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      registered: "📝",
      transit_air: "✈️",
      transit_sea: "🚢",
      arrived: "📦",
      delivering: "🚚",
      delivered: "✅",
      incident: "⚠️",
      pending: "⏳",
      processing: "⚙️",
      cancelled: "❌",
    };
    return icons[status] || "📋";
  };

  const getStatusText = (status) => {
    const texts = {
      registered: "Colis enregistré",
      transit_air: "En transit (aérien)",
      transit_sea: "En transit (maritime)",
      arrived: "Arrivé à destination",
      delivering: "En cours de livraison",
      delivered: "Livré avec succès",
      incident: "Incident détecté",
      pending: "En attente",
      processing: "En traitement",
      cancelled: "Annulé",
    };
    return texts[status] || "Statut inconnu";
  };

  const getStatusColor = (status) => {
    const colors = {
      registered: "#6c757d",
      transit_air: "#0d6efd",
      transit_sea: "#0d6efd",
      arrived: "#ffc107",
      delivering: "#fd7e14",
      delivered: "#198754",
      incident: "#dc3545",
      pending: "#6c757d",
      processing: "#0dcaf0",
      cancelled: "#6c757d",
    };
    return colors[status] || "#6c757d";
  };

  const getStatusDescription = (status) => {
    const descriptions = {
      registered:
        "Votre colis a été enregistré dans notre système et est en attente de traitement.",
      transit_air: "Votre colis est en cours d'acheminement par voie aérienne.",
      transit_sea: "Votre colis est en cours d'acheminement par voie maritime.",
      arrived: "Votre colis est arrivé dans le pays de destination.",
      delivering:
        "Votre colis est en cours de livraison vers l'adresse indiquée.",
      delivered: "Votre colis a été livré avec succès.",
      incident:
        "Un incident a été détecté. Notre équipe vous contactera rapidement.",
      pending: "Votre colis est en attente de prise en charge.",
      processing:
        "Votre colis est en cours de traitement dans notre centre logistique.",
      cancelled: "Ce colis a été annulé.",
    };
    return descriptions[status] || "";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getProgressPercentage = (status) => {
    const progressMap = {
      registered: 10,
      pending: 15,
      processing: 25,
      transit_air: 45,
      transit_sea: 45,
      arrived: 70,
      delivering: 85,
      delivered: 100,
      incident: 50,
      cancelled: 0,
    };
    return progressMap[status] || 0;
  };

  const getDeliveryStatus = () => {
    if (!trackingData) return null;

    const status = trackingData.current_status;
    const expectedDate =
      trackingData.expected_delivery_date || trackingData.estimated_delivery;
    const actualDate = trackingData.actual_delivery_date;
    const isOverdue = trackingData.is_overdue;
    const daysInTransit = trackingData.days_in_transit;

    if (status === "delivered") {
      return {
        type: "delivered",
        message: `Livré le ${formatDateOnly(actualDate)}`,
        icon: "✅",
        color: "#198754",
      };
    } else if (status === "cancelled") {
      return {
        type: "cancelled",
        message: "Colis annulé",
        icon: "❌",
        color: "#6c757d",
      };
    } else if (isOverdue) {
      return {
        type: "overdue",
        message: `En retard - Prévu le ${formatDateOnly(expectedDate)}`,
        icon: "⚠️",
        color: "#dc3545",
      };
    } else if (expectedDate) {
      const today = new Date();
      const expected = new Date(expectedDate);
      const diffTime = expected - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 0) {
        return {
          type: "today",
          message: "Livraison prévue aujourd'hui",
          icon: "📅",
          color: "#fd7e14",
        };
      } else if (diffDays === 1) {
        return {
          type: "tomorrow",
          message: "Livraison prévue demain",
          icon: "📅",
          color: "#0d6efd",
        };
      } else if (diffDays <= 3) {
        return {
          type: "soon",
          message: `Livraison prévue dans ${diffDays} jours (${formatDateOnly(expectedDate)})`,
          icon: "📅",
          color: "#0d6efd",
        };
      } else {
        return {
          type: "scheduled",
          message: `Livraison estimée le ${formatDateOnly(expectedDate)}`,
          icon: "📅",
          color: "#6c757d",
        };
      }
    } else {
      return {
        type: "unknown",
        message: "Date de livraison non communiquée",
        icon: "❓",
        color: "#6c757d",
      };
    }
  };

  const getSEOMeta = () => {
    if (trackingData) {
      return {
        title: `Suivi colis ${trackingData.tracking_number} - ${getStatusText(trackingData.current_status)} | CAC`,
        description: `Suivez votre colis ${trackingData.tracking_number}. Statut actuel : ${getStatusText(trackingData.current_status)}. ${getStatusDescription(trackingData.current_status)}`,
      };
    }
    return {
      title: "Suivi de colis en temps réel | CAC - Chine Afrique Cargo",
      description:
        "Suivez votre colis en temps réel. Entrez votre numéro de suivi pour connaître la position exacte de votre marchandise entre la Chine et l'Afrique.",
    };
  };

  // Vérifier si des données maritimes sont disponibles
  const hasMaritimeData = () => {
    return (
      trackingData &&
      (trackingData.container_number ||
        trackingData.bill_of_lading ||
        trackingData.shipping_line_name ||
        trackingData.loading_port ||
        trackingData.discharging_port ||
        trackingData.current_vessel_name ||
        trackingData.last_location ||
        trackingData.container_type)
    );
  };

  const seoMeta = getSEOMeta();
  const deliveryStatus = trackingData ? getDeliveryStatus() : null;

  if (loadingWhatsapp) {
    return (
      <div className="skeleton-loader">
        <div className="skeleton-spinner"></div>
        <style>{`
                    .skeleton-loader {
                        min-height: 100vh;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%);
                    }
                    .skeleton-spinner {
                        width: 50px;
                        height: 50px;
                        border: 3px solid rgba(198,40,40,0.1);
                        border-top-color: #C62828;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
      </div>
    );
  }

  return (
    <div className="tracking-page">
      <Helmet>
        <title>{seoMeta.title}</title>
        <meta name="description" content={seoMeta.description} />
        <meta
          name="keywords"
          content="suivi colis, tracking CAC, transport chine afrique, localisation colis"
        />
        <meta name="author" content="CAC - Chine Afrique Cargo" />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={
            trackingData
              ? `https://cac-int.com/tracking/${trackingData.tracking_number}`
              : "https://cac-int.com/tracking"
          }
        />
        {trackingData && <meta name="robots" content="noindex, follow" />}
      </Helmet>

      {/* Hero Section */}
      <div className="tracking-hero">
        <div className="hero-overlay"></div>
        <Container className="hero-container">
          <div className={`hero-content ${animated ? "fade-in-up" : ""}`}>
            <div className="hero-badge">
              <span className="badge-pulse">📍 Suivi en temps réel</span>
            </div>
            <h1 className="hero-title">
              Suivi de <span className="text-gradient">colis</span>
            </h1>
            <p className="hero-subtitle">
              Entrez votre numéro de suivi pour connaître la position exacte de
              votre colis
            </p>
            <Form onSubmit={handleSubmit} className="tracking-form" noValidate>
              <div className="search-wrapper">
                <div className="search-input-group">
                  <span className="search-icon" aria-hidden="true">
                    🔍
                  </span>
                  <input
                    type="text"
                    placeholder="Ex: CAC12345678"
                    value={trackingInput}
                    onChange={handleInputChange}
                    className={`search-input ${inputError ? "is-invalid" : ""}`}
                    aria-label="Numéro de suivi"
                    aria-invalid={!!inputError}
                    aria-describedby={inputError ? "tracking-error" : undefined}
                    autoComplete="off"
                    spellCheck="false"
                  />
                  <button
                    type="submit"
                    className="search-btn"
                    disabled={loading}
                    aria-label="Rechercher le colis"
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" role="status" />
                    ) : (
                      "Suivre"
                    )}
                  </button>
                </div>
                {inputError && (
                  <div
                    id="tracking-error"
                    className="input-error-message"
                    role="alert"
                  >
                    <span className="error-icon">⚠️</span> {inputError}
                  </div>
                )}
              </div>
              <div className="tracking-hint">
                <span>💡 Exemples de formats : CAC12345678, CACABCD1234</span>
              </div>
            </Form>
          </div>
        </Container>
        <div className="hero-wave">
          <svg
            viewBox="0 0 1440 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 64L60 69.3C120 75 240 85 360 80C480 75 600 53 720 48C840 43 960 53 1080 58.7C1200 64 1320 64 1380 64L1440 64L1440 120L1380 120C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120L0 120Z"
              fill="#f8f9fa"
            />
          </svg>
        </div>
      </div>

      {/* Results Section */}
      <Container className="results-container">
        {error && !trackingData && (
          <div className="error-card" role="alert">
            <div className="error-icon" aria-hidden="true">
              ❌
            </div>
            <h4>{error}</h4>
            <p>Vérifiez le numéro et réessayez</p>
            <div className="error-suggestions">
              <p>Conseils :</p>
              <ul>
                <li>Vérifiez que le numéro commence par "CAC"</li>
                <li>Assurez-vous qu'il n'y a pas d'espaces</li>
                <li>Le numéro se trouve sur votre confirmation de commande</li>
              </ul>
            </div>
          </div>
        )}

        {loading && (
          <div className="loading-container" role="status" aria-live="polite">
            <div className="loading-spinner"></div>
            <p>Recherche de votre colis en cours...</p>
            <small className="loading-subtext">
              Cela peut prendre quelques secondes
            </small>
          </div>
        )}

        {trackingData && !loading && (
          <div
            className={`results-content ${animated ? "fade-in-up" : ""}`}
            aria-live="polite"
          >
            {/* Progress Bar */}
            <div className="progress-section">
              <div className="progress-header">
                <span>Progression de la livraison</span>
                <span className="progress-percentage">
                  {getProgressPercentage(trackingData.current_status)}%
                </span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${getProgressPercentage(trackingData.current_status)}%`,
                    background: getStatusColor(trackingData.current_status),
                  }}
                ></div>
              </div>
            </div>

            {/* Status Header Card */}
            <div className="status-header-card">
              <div className="status-icon" aria-hidden="true">
                {getStatusIcon(trackingData.current_status)}
              </div>
              <div className="status-info">
                <div className="tracking-number-wrapper">
                  <span className="tracking-label">Numéro de suivi</span>
                  <div className="tracking-number">
                    <span className="number">
                      {trackingData.tracking_number}
                    </span>
                    <button
                      className="copy-btn"
                      onClick={copyToClipboard}
                      aria-label="Copier le numéro de suivi"
                      title="Copier le numéro"
                    >
                      {copied ? "✓" : "📋"}
                    </button>
                  </div>
                </div>
                <div className="status-with-description">
                  <div
                    className="status-badge"
                    style={{
                      backgroundColor: getStatusColor(
                        trackingData.current_status,
                      ),
                    }}
                    role="status"
                  >
                    {getStatusText(trackingData.current_status)}
                  </div>
                  <p className="status-description">
                    {getStatusDescription(trackingData.current_status)}
                  </p>
                </div>
              </div>
            </div>

            {/* Delivery Estimate Card */}
            {deliveryStatus && (
              <div
                className="delivery-estimate-card"
                style={{ borderLeftColor: deliveryStatus.color }}
              >
                <div
                  className="delivery-icon"
                  style={{ color: deliveryStatus.color }}
                >
                  {deliveryStatus.icon}
                </div>
                <div className="delivery-info">
                  <span className="delivery-label">Livraison estimée</span>
                  <span
                    className="delivery-message"
                    style={{ color: deliveryStatus.color }}
                  >
                    {deliveryStatus.message}
                  </span>
                </div>
                {trackingData.days_in_transit > 0 && (
                  <div className="delivery-transit">
                    <span className="transit-badge">
                      📦 {trackingData.days_in_transit} jour
                      {trackingData.days_in_transit > 1 ? "s" : ""} en transit
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ⭐ SECTION TRACKING MARITIME JSONCARGO - UNIFORMISÉE */}
            {hasMaritimeData() && (
              <div className="maritime-card">
                <div className="maritime-header">
                  <span className="maritime-icon">🚢</span>
                  <h3 className="maritime-title">Informations maritimes</h3>
                  <span className="maritime-badge">Données en temps réel</span>
                </div>

                <div className="maritime-content">
                  {/* Numéro de conteneur */}
                  {trackingData.container_number && (
                    <div className="maritime-item">
                      <div className="maritime-label">
                        🚛 Numéro de conteneur
                      </div>
                      <div className="maritime-value">
                        <code>{trackingData.container_number}</code>
                        <button
                          className="copy-small-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              trackingData.container_number,
                            );
                            toast.success("Numéro de conteneur copié !");
                          }}
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Packing List No. */}
                  {trackingData.bill_of_lading && (
                    <div className="maritime-item">
                      <div className="maritime-label">📄 Packing List No.</div>
                      <div className="maritime-value">
                        <code>{trackingData.bill_of_lading}</code>
                        <button
                          className="copy-small-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              trackingData.bill_of_lading,
                            );
                            toast.success("Packing List No. copié !");
                          }}
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Compagnie maritime */}
                  {trackingData.shipping_line_name && (
                    <div className="maritime-item">
                      <div className="maritime-label">
                        🏢 Compagnie maritime
                      </div>
                      <div className="maritime-value">
                        {trackingData.shipping_line_name}
                      </div>
                    </div>
                  )}

                  {/* Type de conteneur */}
                  {trackingData.container_type && (
                    <div className="maritime-item">
                      <div className="maritime-label">📦 Type de conteneur</div>
                      <div className="maritime-value">
                        {trackingData.container_type}
                      </div>
                    </div>
                  )}

                  {/* ⭐ PORTS - CHARGEMENT ET DÉCHARGEMENT */}
                  <Row className="g-3">
                    {trackingData.loading_port && (
                      <Col md={6}>
                        <div className="maritime-port">
                          <div className="port-icon">📤</div>
                          <div className="port-info">
                            <span className="port-label">
                              Port de chargement
                            </span>
                            <span className="port-name">
                              {trackingData.loading_port}
                            </span>
                            {trackingData.loading_port_terminal && (
                              <small className="port-terminal">
                                {trackingData.loading_port_terminal}
                              </small>
                            )}
                            {trackingData.atd_origin && (
                              <span className="port-date">
                                Départ: {formatDate(trackingData.atd_origin)}
                              </span>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}

                    {trackingData.discharging_port && (
                      <Col md={6}>
                        <div className="maritime-port">
                          <div className="port-icon">📥</div>
                          <div className="port-info">
                            <span className="port-label">
                              Port de déchargement
                            </span>
                            <span className="port-name">
                              {trackingData.discharging_port}
                            </span>
                            {trackingData.discharging_port_terminal && (
                              <small className="port-terminal">
                                {trackingData.discharging_port_terminal}
                              </small>
                            )}
                            {trackingData.eta_final_destination && (
                              <span className="port-date">
                                ETA:{" "}
                                {formatDate(trackingData.eta_final_destination)}
                              </span>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>

                  {/* ⭐ NAVIRE */}
                  {(trackingData.current_vessel_name ||
                    trackingData.last_vessel_name) && (
                    <div className="maritime-vessel">
                      <div className="vessel-header">
                        <span className="vessel-icon">⛴️</span>
                        <span className="vessel-label">Navire</span>
                      </div>
                      <div className="vessel-info">
                        {trackingData.current_vessel_name && (
                          <div className="vessel-name">
                            <strong></strong> {trackingData.current_vessel_name}
                            {trackingData.current_voyage_number &&
                              ` (Voyage: ${trackingData.current_voyage_number})`}
                          </div>
                        )}
                        {trackingData.last_vessel_name &&
                          trackingData.current_vessel_name !==
                            trackingData.last_vessel_name && (
                            <div className="vessel-previous">
                              <span>Précédent navire:</span>{" "}
                              {trackingData.last_vessel_name}
                              {trackingData.last_voyage_number &&
                                ` (Voyage: ${trackingData.last_voyage_number})`}
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {/* Localisation actuelle */}
                  {trackingData.last_location && (
                    <div className="maritime-location">
                      <div className="location-header">
                        <span className="location-icon">📍</span>
                        <span className="location-label">
                          Localisation actuelle
                        </span>
                      </div>
                      <div className="location-info">
                        <div className="location-name">
                          {trackingData.last_location}
                        </div>
                        {trackingData.last_location_terminal && (
                          <div className="location-terminal">
                            {trackingData.last_location_terminal}
                          </div>
                        )}
                        {trackingData.atd_last_location && (
                          <div className="location-date">
                            Départ: {formatDate(trackingData.atd_last_location)}
                          </div>
                        )}
                        {trackingData.eta_next_destination && (
                          <div className="location-eta">
                            Prochaine ETA:{" "}
                            {formatDate(trackingData.eta_next_destination)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Douane */}
                  {trackingData.customs_clearance && (
                    <div className="maritime-customs">
                      <span className="customs-icon">📋</span>
                      <div>
                        <div className="customs-label">Dédouanement</div>
                        <div className="customs-date">
                          {formatDate(trackingData.customs_clearance)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Statut API */}
                  {trackingData.api_container_status && (
                    <div className="maritime-status">
                      <span className="status-icon">📡</span>
                      <div>
                        <div className="status-label">Statut API</div>
                        <div className="status-value">
                          {trackingData.api_container_status}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Info Cards */}
            <Row className="g-4 mb-4">
              <Col md={6}>
                <div className="info-card">
                  <div className="info-icon" aria-hidden="true">
                    👤
                  </div>
                  <div className="info-content">
                    <h6>Destinataire</h6>
                    <p className="mb-1">
                      <strong>
                        {trackingData.recipient_name ||
                          trackingData.recipent_name ||
                          "Non spécifié"}
                      </strong>
                    </p>
                    <small className="text-muted">
                      {trackingData.recipient_address ||
                        trackingData.recipent_address ||
                        "-"}
                    </small>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="info-card">
                  <div className="info-icon" aria-hidden="true">
                    📅
                  </div>
                  <div className="info-content">
                    <h6>Informations</h6>
                    <div className="info-row">
                      <span>Date d'envoi :</span>
                      <strong>{formatDate(trackingData.created_at)}</strong>
                    </div>
                    <div className="info-row">
                      <span>Dernière mise à jour :</span>
                      <strong>
                        {trackingData.history?.length > 0
                          ? formatDate(
                              trackingData.history[
                                trackingData.history.length - 1
                              ].changed_at,
                            )
                          : formatDate(
                              trackingData.updated_at ||
                                trackingData.created_at,
                            )}
                      </strong>
                    </div>
                    {(trackingData.expected_delivery_date ||
                      trackingData.estimated_delivery) && (
                      <div className="info-row estimated">
                        <span>Livraison estimée :</span>
                        <strong className="text-success">
                          {formatDateOnly(
                            trackingData.expected_delivery_date ||
                              trackingData.estimated_delivery,
                          )}
                        </strong>
                      </div>
                    )}
                    {trackingData.actual_delivery_date && (
                      <div className="info-row delivered">
                        <span>Livré le :</span>
                        <strong className="text-success">
                          {formatDate(trackingData.actual_delivery_date)}
                        </strong>
                      </div>
                    )}
                    {trackingData.shipment_type_display && (
                      <div className="info-row">
                        <span>Type d'expédition :</span>
                        <strong>{trackingData.shipment_type_display}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Package Details */}
            {trackingData.package_details &&
              Object.keys(trackingData.package_details).length > 0 && (
                <div className="package-card">
                  <h4 className="package-title">📦 Détails du colis</h4>
                  <Row className="g-3">
                    {trackingData.package_details.weight && (
                      <Col xs={6} md={3}>
                        <div className="package-item">
                          <span className="package-label">Poids</span>
                          <span className="package-value">
                            {trackingData.package_details.weight}{" "}
                            {trackingData.package_details.weight_unit || "kg"}
                          </span>
                        </div>
                      </Col>
                    )}
                    {trackingData.package_details.volume && (
                      <Col xs={6} md={3}>
                        <div className="package-item">
                          <span className="package-label">Volume</span>
                          <span className="package-value">
                            {trackingData.package_details.volume}{" "}
                            {trackingData.package_details.volume_unit || "CBM"}
                          </span>
                        </div>
                      </Col>
                    )}
                    {trackingData.package_details.dimensions && (
                      <Col xs={6} md={3}>
                        <div className="package-item">
                          <span className="package-label">Dimensions</span>
                          <span className="package-value">
                            {trackingData.package_details.dimensions}
                          </span>
                        </div>
                      </Col>
                    )}
                    {trackingData.package_details.quantity && (
                      <Col xs={6} md={3}>
                        <div className="package-item">
                          <span className="package-label">Quantité</span>
                          <span className="package-value">
                            {trackingData.package_details.quantity}
                          </span>
                        </div>
                      </Col>
                    )}
                    {trackingData.package_details.type && (
                      <Col xs={6} md={3}>
                        <div className="package-item">
                          <span className="package-label">Type</span>
                          <span className="package-value">
                            {trackingData.package_details.type}
                          </span>
                        </div>
                      </Col>
                    )}
                    {trackingData.package_details.description && (
                      <Col xs={12}>
                        <div className="package-item package-description">
                          <span className="package-label">Description</span>
                          <span className="package-value">
                            {trackingData.package_details.description}
                          </span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              )}

            {/* Timeline */}
            <div className="timeline-card">
              <h3 className="timeline-title">📍 Historique du colis</h3>
              <div className="timeline" role="list">
                {trackingData.history?.length > 0 ? (
                  trackingData.history.map((item, index) => (
                    <div key={index} className="timeline-item" role="listitem">
                      <div className="timeline-marker">
                        <div
                          className={`marker-dot ${item.status === trackingData.current_status ? "active" : ""}`}
                          aria-hidden="true"
                        >
                          {getStatusIcon(item.status)}
                        </div>
                        {index < trackingData.history.length - 1 && (
                          <div className="marker-line"></div>
                        )}
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span
                            className="timeline-status"
                            style={{
                              backgroundColor: getStatusColor(item.status),
                            }}
                          >
                            {item.status_display || getStatusText(item.status)}
                          </span>
                          <span className="timeline-date">
                            {formatDate(item.changed_at)}
                          </span>
                        </div>
                        {item.location && (
                          <p className="timeline-location">
                            <span className="location-icon">📍</span>{" "}
                            {item.location}
                          </p>
                        )}
                        {item.comment && (
                          <p className="timeline-comment">{item.comment}</p>
                        )}
                        {item.changed_by_name && (
                          <p className="timeline-agent">
                            <span className="agent-icon">👤</span>{" "}
                            {item.changed_by_name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-timeline">
                    <span className="empty-icon">📋</span>
                    <p>Aucun historique disponible pour le moment</p>
                    <small>
                      Les mises à jour apparaîtront ici dès que le colis sera
                      traité
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Support Card */}
            <div className="support-card">
              <div className="support-icon" aria-hidden="true">
                💬
              </div>
              <div className="support-content">
                <h5>Besoin d'aide ?</h5>
                <p>
                  Notre équipe est à votre disposition pour toute question
                  concernant votre colis
                </p>
                <div className="support-buttons">
                  <a
                    href={`https://wa.me/${cleanPhoneNumber(whatsappNumber)}?text=Bonjour, je souhaite des informations sur le colis ${trackingData.tracking_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="support-btn whatsapp"
                    aria-label="Contacter le support sur WhatsApp"
                  >
                    📱 WhatsApp
                  </a>
                  <Link to="/contact" className="support-btn contact">
                    📧 Nous contacter
                  </Link>
                </div>
              </div>
            </div>

            {/* Share Section */}
            <div className="share-section">
              <p className="share-label">Partager ce suivi :</p>
              <div className="share-buttons">
                <button
                  className="share-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("✅ Lien copié dans le presse-papier !");
                  }}
                  aria-label="Copier le lien de suivi"
                >
                  🔗 Copier le lien
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Suivez mon colis CAC : ${window.location.href}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="share-btn whatsapp-share"
                  aria-label="Partager sur WhatsApp"
                >
                  💬 Partager
                </a>
              </div>
            </div>
          </div>
        )}
      </Container>

      {/* Modal pour la carte GPS */}
      <Modal
        show={showMapModal}
        onHide={() => setShowMapModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>🗺️ Position du conteneur</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLocation && (
            <div className="map-container">
              <iframe
                title="Carte de localisation"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedLocation.lng - 1},${selectedLocation.lat - 1},${selectedLocation.lng + 1},${selectedLocation.lat + 1}&layer=mapnik&marker=${selectedLocation.lat},${selectedLocation.lng}`}
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: "12px" }}
                allowFullScreen
              />
              <div className="map-coords mt-3 text-center">
                <p>
                  <strong>Coordonnées GPS :</strong>
                  <br />
                  Latitude: {selectedLocation.lat}
                  <br />
                  Longitude: {selectedLocation.lng}
                </p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    window.open(
                      `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`,
                      "_blank",
                    );
                  }}
                >
                  🌍 Ouvrir dans Google Maps
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <style>{`
        .tracking-page {
          min-height: 100vh;
          background: #f8f9fa;
        }

        /* Hero Section */
        .tracking-hero {
          position: relative;
          background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
          min-height: 450px;
          display: flex;
          align-items: center;
          overflow: hidden;
        }

        .hero-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 50%, rgba(198, 40, 40, 0.2) 0%, transparent 50%);
        }

        .hero-container {
          position: relative;
          z-index: 2;
          padding: 60px 0;
        }

        .hero-content {
          text-align: center;
          max-width: 700px;
          margin: 0 auto;
        }

        .hero-badge {
          margin-bottom: 24px;
        }

        .badge-pulse {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(249, 168, 37, 0.15);
          border: 1px solid rgba(249, 168, 37, 0.3);
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          color: #F9A825;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(249, 168, 37, 0); }
          100% { box-shadow: 0 0 0 0 rgba(249, 168, 37, 0); }
        }

        .hero-title {
          font-size: 56px;
          font-weight: 800;
          margin-bottom: 20px;
          color: white;
        }

        .text-gradient {
          background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 40px;
        }

        .tracking-form {
          max-width: 550px;
          margin: 0 auto;
        }

        .search-wrapper {
          background: white;
          border-radius: 60px;
          padding: 4px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
        }

        .search-input-group {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 60px;
          overflow: hidden;
        }

        .search-icon {
          padding: 0 16px;
          font-size: 18px;
        }

        .search-input {
          flex: 1;
          border: none;
          padding: 16px 0;
          font-size: 16px;
          outline: none;
          text-transform: uppercase;
          font-family: monospace;
          letter-spacing: 1px;
        }

        .search-input.is-invalid {
          color: #C62828;
        }

        .search-input::placeholder {
          text-transform: none;
          font-family: inherit;
          letter-spacing: normal;
          color: #999;
        }

        .search-btn {
          background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
          border: none;
          padding: 12px 32px;
          border-radius: 50px;
          color: white;
          font-weight: 600;
          margin: 4px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .search-btn:hover:not(:disabled) {
          transform: scale(1.02);
          box-shadow: 0 5px 15px rgba(198, 40, 40, 0.4);
        }

        .search-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .input-error-message {
          color: #ff6b6b;
          font-size: 13px;
          margin-top: 8px;
          padding-left: 20px;
          display: flex;
          align-items: center;
          gap: 6px;
          animation: fadeIn 0.3s ease;
        }

        .error-icon {
          font-size: 14px;
        }

        .tracking-hint {
          margin-top: 12px;
          color: rgba(255, 255, 255, 0.6);
          font-size: 13px;
        }

        .hero-wave {
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
        }

        /* Results Container */
        .results-container {
          padding: 60px 0;
          position: relative;
          z-index: 2;
        }

        /* Progress Section */
        .progress-section {
          background: white;
          border-radius: 16px;
          padding: 20px 24px;
          margin-bottom: 24px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          font-weight: 500;
        }

        .progress-percentage {
          color: #C62828;
          font-weight: 700;
        }

        .progress-bar-container {
          height: 8px;
          background: #e0e0e0;
          border-radius: 10px;
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          border-radius: 10px;
          transition: width 0.6s ease;
        }

        /* Error Card */
        .error-card {
          background: white;
          border-radius: 24px;
          padding: 40px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .error-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .error-card h4 {
          color: #C62828;
          margin-bottom: 10px;
        }

        .error-suggestions {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #eee;
          text-align: left;
        }

        .error-suggestions p {
          font-weight: 600;
          margin-bottom: 8px;
        }

        .error-suggestions ul {
          color: #666;
          padding-left: 20px;
        }

        .error-suggestions li {
          margin-bottom: 6px;
        }

        /* Loading Container */
        .loading-container {
          text-align: center;
          padding: 60px;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #C62828;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        .loading-subtext {
          display: block;
          margin-top: 8px;
          color: #999;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Animations */
        .fade-in-up {
          animation: fadeInUp 0.6s ease forwards;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Status Header Card */
        .status-header-card {
          background: white;
          border-radius: 24px;
          padding: 30px;
          display: flex;
          align-items: flex-start;
          gap: 24px;
          margin-bottom: 24px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .status-icon {
          font-size: 64px;
          background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .status-info {
          flex: 1;
        }

        .tracking-number-wrapper {
          margin-bottom: 16px;
        }

        .tracking-label {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .tracking-number {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .tracking-number .number {
          font-size: 28px;
          font-weight: 800;
          font-family: monospace;
          color: #C62828;
          letter-spacing: 1px;
        }

        .copy-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .copy-btn:hover {
          background: #f0f0f0;
        }

        .status-with-description {
          margin-top: 12px;
        }

        .status-badge {
          display: inline-block;
          padding: 8px 24px;
          border-radius: 50px;
          color: white;
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 12px;
        }

        .status-description {
          color: #666;
          font-size: 14px;
          line-height: 1.6;
          margin: 0;
        }

        /* Delivery Estimate Card */
        .delivery-estimate-card {
          background: white;
          border-radius: 16px;
          padding: 20px 24px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border-left: 4px solid;
        }

        .delivery-icon {
          font-size: 32px;
        }

        .delivery-info {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .delivery-label {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 4px;
        }

        .delivery-message {
          font-size: 18px;
          font-weight: 600;
        }

        .delivery-transit {
          margin-left: auto;
        }

        .transit-badge {
          background: #f0f0f0;
          padding: 6px 16px;
          border-radius: 40px;
          font-size: 14px;
          font-weight: 500;
          color: #666;
        }

        /* Info Cards */
        .info-card {
          background: white;
          border-radius: 20px;
          padding: 24px;
          display: flex;
          gap: 16px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          height: 100%;
        }

        .info-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
        }

        .info-icon {
          font-size: 32px;
          flex-shrink: 0;
        }

        .info-content {
          flex: 1;
        }

        .info-content h6 {
          font-weight: 700;
          margin-bottom: 12px;
          color: #C62828;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .info-row span {
          color: #666;
        }

        .info-row.estimated {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #e0e0e0;
        }

        .info-row.estimated strong {
          color: #198754;
        }

        .info-row.delivered {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #e0e0e0;
        }

        .info-row.delivered strong {
          color: #198754;
        }

        /* Package Card */
        .package-card {
          background: white;
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
        }

        .package-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 20px;
          color: #C62828;
        }

        .package-item {
          text-align: center;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 12px;
          height: 100%;
        }

        .package-item.package-description {
          text-align: left;
        }

        .package-label {
          display: block;
          font-size: 12px;
          color: #999;
          margin-bottom: 4px;
        }

        .package-value {
          font-weight: 700;
          color: #333;
        }

        /* Timeline Card */
        .timeline-card {
          background: white;
          border-radius: 24px;
          padding: 30px;
          margin-bottom: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .timeline-title {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 30px;
          color: #C62828;
        }

        .timeline {
          position: relative;
        }

        .timeline-item {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
        }

        .timeline-marker {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .marker-dot {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          position: relative;
          z-index: 2;
          transition: all 0.3s ease;
        }

        .marker-dot.active {
          background: linear-gradient(135deg, #C62828 0%, #F9A825 100%);
          animation: pulse 1.5s infinite;
        }

        .marker-line {
          position: absolute;
          top: 48px;
          width: 2px;
          height: calc(100% + 30px);
          background: #e0e0e0;
        }

        .timeline-content {
          flex: 1;
          padding-bottom: 20px;
        }

        .timeline-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 10px;
        }

        .timeline-status {
          display: inline-block;
          padding: 4px 16px;
          border-radius: 20px;
          color: white;
          font-size: 12px;
          font-weight: 600;
        }

        .timeline-date {
          font-size: 12px;
          color: #999;
        }

        .timeline-location {
          font-size: 13px;
          color: #666;
          margin: 8px 0 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .timeline-comment {
          font-size: 14px;
          color: #666;
          margin: 0;
          padding-top: 8px;
        }

        .timeline-agent {
          font-size: 12px;
          color: #999;
          margin: 8px 0 0;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .agent-icon {
          font-size: 12px;
        }

        .empty-timeline {
          text-align: center;
          padding: 40px;
          color: #999;
        }

        .empty-icon {
          font-size: 48px;
          display: block;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-timeline p {
          margin-bottom: 8px;
          font-size: 16px;
        }

        .empty-timeline small {
          color: #bbb;
        }

        /* Support Card */
        .support-card {
          background: linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%);
          border-radius: 20px;
          padding: 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
          margin-bottom: 24px;
        }

        .support-icon {
          font-size: 48px;
        }

        .support-content {
          flex: 1;
        }

        .support-content h5 {
          font-weight: 700;
          margin-bottom: 8px;
        }

        .support-content p {
          color: #666;
          margin-bottom: 16px;
        }

        .support-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .support-btn {
          padding: 8px 24px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s ease;
          display: inline-block;
        }

        .support-btn.whatsapp {
          background: #25D366;
          color: white;
        }

        .support-btn.whatsapp:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(37, 211, 102, 0.4);
          color: white;
        }

        .support-btn.contact {
          background: #C62828;
          color: white;
        }

        .support-btn.contact:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(198, 40, 40, 0.4);
          color: white;
        }

        /* Share Section */
        .share-section {
          background: white;
          border-radius: 16px;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
        }

        .share-label {
          margin: 0;
          font-weight: 600;
          color: #333;
        }

        .share-buttons {
          display: flex;
          gap: 12px;
        }

        .share-btn {
          background: #f0f0f0;
          border: none;
          padding: 8px 20px;
          border-radius: 40px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #333;
          text-decoration: none;
          display: inline-block;
        }

        .share-btn:hover {
          background: #e0e0e0;
          transform: translateY(-2px);
        }

        .share-btn.whatsapp-share {
          background: #25D366;
          color: white;
        }

        .share-btn.whatsapp-share:hover {
          background: #20b859;
          box-shadow: 0 5px 15px rgba(37, 211, 102, 0.4);
        }

        /* ⭐ Maritime Card Styles - UNIFORMISÉES AVEC LE RESTE DE LA PAGE */
        .maritime-card {
          background: white;
          border-radius: 24px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
        }

        .maritime-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f0f0f0;
        }

        .maritime-icon {
          font-size: 32px;
        }

        .maritime-title {
          font-size: 20px;
          font-weight: 700;
          color: #C62828;
          margin: 0;
          flex: 1;
        }

        .maritime-badge {
          background: rgba(198, 40, 40, 0.1);
          color: #C62828;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
        }

        .maritime-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .maritime-item {
          background: #f8f9fa;
          border-radius: 16px;
          padding: 14px 16px;
        }

        .maritime-label {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 6px;
        }

        .maritime-value {
          font-size: 15px;
          color: #333;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .maritime-value code {
          background: #f0f0f0;
          padding: 4px 12px;
          border-radius: 8px;
          font-family: monospace;
          font-size: 14px;
          color: #C62828;
        }

        .copy-small-btn {
          background: none;
          border: none;
          color: #999;
          cursor: pointer;
          font-size: 14px;
          padding: 4px 8px;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .copy-small-btn:hover {
          background: #e0e0e0;
          color: #333;
        }

        .maritime-port {
          display: flex;
          gap: 16px;
          padding: 14px;
          background: #f8f9fa;
          border-radius: 16px;
          height: 100%;
        }

        .port-icon {
          font-size: 28px;
        }

        .port-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .port-label {
          font-size: 11px;
          color: #999;
          text-transform: uppercase;
        }

        .port-name {
          font-size: 15px;
          font-weight: 600;
          color: #333;
        }

        .port-terminal {
          font-size: 12px;
          color: #888;
        }

        .port-date {
          font-size: 11px;
          color: #C62828;
          margin-top: 4px;
        }

        .maritime-vessel {
          background: #f8f9fa;
          border-radius: 16px;
          padding: 14px 16px;
        }

        .vessel-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
        }

        .vessel-icon {
          font-size: 20px;
        }

        .vessel-label {
          font-size: 12px;
          color: #999;
          text-transform: uppercase;
        }

        .vessel-info {
          padding-left: 28px;
        }

        .vessel-name {
          color: #333;
          margin-bottom: 6px;
          font-size: 14px;
        }

        .vessel-previous {
          font-size: 13px;
          color: #888;
        }

        .maritime-location {
          background: #e8f5e9;
          border-radius: 16px;
          padding: 14px 16px;
          border-left: 3px solid #4CAF50;
        }

        .location-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
        }

        .location-icon {
          font-size: 20px;
        }

        .location-label {
          font-size: 12px;
          color: #2E7D32;
          text-transform: uppercase;
          font-weight: 600;
        }

        .location-info {
          padding-left: 28px;
        }

        .location-name {
          font-size: 16px;
          font-weight: 700;
          color: #333;
          margin-bottom: 4px;
        }

        .location-terminal {
          font-size: 12px;
          color: #888;
          margin-bottom: 6px;
        }

        .location-date,
        .location-eta {
          font-size: 11px;
          color: #C62828;
        }

        .maritime-customs {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #e3f2fd;
          border-radius: 12px;
        }

        .customs-icon {
          font-size: 20px;
        }

        .customs-label {
          font-size: 11px;
          color: #0d6efd;
          text-transform: uppercase;
        }

        .customs-date {
          font-size: 14px;
          font-weight: 500;
          color: #333;
        }

        .maritime-status {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: #f5f5f5;
          border-radius: 12px;
        }

        .status-label {
          font-size: 11px;
          color: #999;
          text-transform: uppercase;
        }

        .status-value {
          font-size: 13px;
          color: #333;
        }

        .map-container {
          width: 100%;
        }

        .map-coords {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 8px;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .hero-title {
            font-size: 40px;
          }

          .status-header-card {
            flex-direction: column;
            text-align: center;
            align-items: center;
          }

          .tracking-number {
            justify-content: center;
          }

          .timeline-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .share-section {
            flex-direction: column;
            text-align: center;
          }

          .delivery-estimate-card {
            flex-direction: column;
            text-align: center;
          }

          .delivery-transit {
            margin-left: 0;
          }
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: 32px;
          }

          .tracking-number .number {
            font-size: 20px;
          }

          .info-card {
            flex-direction: column;
            text-align: center;
          }

          .info-row {
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .support-card {
            flex-direction: column;
            text-align: center;
          }

          .support-buttons {
            justify-content: center;
          }

          .progress-section {
            padding: 16px;
          }

          .timeline-card {
            padding: 20px;
          }

          .marker-dot {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }

          .delivery-message {
            font-size: 16px;
          }

          .maritime-port {
            flex-direction: column;
            text-align: center;
          }

          .port-info {
            align-items: center;
          }

          .location-info {
            padding-left: 0;
          }
        }

        @media (max-width: 576px) {
          .hero-title {
            font-size: 28px;
          }

          .search-input {
            font-size: 14px;
            padding: 14px 0;
          }

          .search-btn {
            padding: 10px 20px;
            font-size: 14px;
          }

          .status-icon {
            width: 80px;
            height: 80px;
            font-size: 48px;
          }

          .tracking-number .number {
            font-size: 18px;
          }
        }
      `}</style>
    </div>
  );
};

export default TrackingPage;
